import { CommonModule } from '@angular/common';
import { Component, Input, Output, EventEmitter, input, computed } from '@angular/core';
import { RouterModule, UrlTree } from '@angular/router';
import { ChitinIconComponent } from '@shared/components/chitin/atoms/icon/chitin-icon.component';

const SIZE_SANITIZATION_MAP: Record<'xs' | 'small' | 'medium' | 'large', 'xs' | 'sm' | 'md' | 'lg'> = {
  xs: 'xs',
  small: 'sm',
  medium: 'md',
  large: 'lg',
};

@Component({
  selector: 'chitin-button',
  templateUrl: './button.component.html',
  styleUrls: ['./button.component.scss'],
  imports: [CommonModule, ChitinIconComponent, RouterModule],
})
export class ChitinButtonComponent {
  @Input()
  disabled: boolean = false;

  @Input()
  isLoading: boolean = false;

  @Input()
  outline: boolean = false;

  @Input()
  color: 'primary' | 'danger' | 'success' | 'secondary' | 'text' | 'lighter-blue' | 'pink' | 'light' | 'gray' = 'primary';

  @Input()
  variant: 'text' | 'button' = 'button';

  size = input<'xs' | 'small' | 'medium' | 'large'>('medium');
  sizeSanitized = computed(() => SIZE_SANITIZATION_MAP[this.size()]);

  @Input()
  type: 'button' | 'submit' | 'reset' = 'button';

  @Input()
  label: string | undefined = 'Button';

  icon = input<string>();

  @Input()
  iconPosition: 'left' | 'right' | 'center' = 'left';

  @Input()
  fullwidth?: boolean = false;

  @Input()
  routerLink?: string[] | string | UrlTree | null;

  /**
   * Optional click handler
   */
  @Output()
  clicked = new EventEmitter<Event>();

  public onClick(event: MouseEvent): void {
    if (this.disabled || this.isLoading) return;
    this.clicked.emit(event);
    event.stopPropagation();
  }

  protected get classes(): string[] {
    const classes = [this.getButtonClass()];
    const buttonSizeClass = this.getButtonSizeClass();
    if (buttonSizeClass) classes.push(buttonSizeClass);
    if (this.disabled || this.isLoading) classes.push('disabled');
    if (this.isLoading) classes.push('loading');
    if (this.fullwidth) classes.push('w-100');
    if (this.color === 'success' && !this.outline) classes.push('text-white');
    return classes;
  }

  private getButtonSizeClass(): string | undefined {
    if (this.sizeSanitized() === 'md') return undefined;
    return `btn-${this.sizeSanitized()}`;
  }

  private getButtonClass(): string {
    return this.outline ? `btn-outline-${this.color}` : `btn-${this.color}`;
  }
}
